import { useCallback, useEffect, useState } from 'react';
import type { Theme } from '../../shared/types';

const STORAGE_KEY = 'ghostwire-ai:theme';

function readStored(): Theme | null {
  if (typeof localStorage === 'undefined') return null;
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'dark' || v === 'light' ? v : null;
}

function systemPrefers(): Theme {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches
    ? 'light'
    : 'dark';
}

function apply(theme: Theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.dataset.theme = theme;
}

/**
 * Hook returning the current theme + setters. Persists to localStorage and
 * applies the `.dark` class on `<html>`. Default precedence:
 *   1. stored preference (localStorage)
 *   2. user_configs.theme from the server (set externally via setTheme)
 *   3. system preference (prefers-color-scheme)
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(
    () => readStored() ?? systemPrefers(),
  );

  useEffect(() => {
    apply(theme);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, theme);
    }
  }, [theme]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const toggle = useCallback(
    () => setThemeState((t) => (t === 'dark' ? 'light' : 'dark')),
    [],
  );

  return { theme, setTheme, toggle };
}
